import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Heart, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FullProfileBody } from "@/components/matchmaking/full-profile";
import { getMatchCandidateProfile, swipeMatch, type MatchIntent } from "@/lib/boide-api";

type ProfileSearch = { type?: MatchIntent };

export const Route = createFileRoute("/_authenticated/se-duyen/$handle")({
  validateSearch: (search: Record<string, unknown>): ProfileSearch => ({
    type: search.type === "lam_an" ? "lam_an" : "se_duyen",
  }),
  component: CandidateProfilePage,
});

function CandidateProfilePage() {
  const { handle } = Route.useParams();
  const { type = "se_duyen" } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["mm-candidate-profile", handle, type],
    queryFn: () => getMatchCandidateProfile(handle, type),
  });

  const swipe = useMutation({
    mutationFn: (action: "like" | "pass") => {
      const targetId = q.data?.user_id;
      if (!targetId) throw new Error("Chưa tải xong hồ sơ");
      return swipeMatch({ target_user_id: targetId, type, action });
    },
    onSuccess: (res) => {
      if (res.matched) {
        toast.success("Tâm đầu ý hợp! Hai bạn đã kết nối 💞");
        qc.invalidateQueries({ queryKey: ["mm-connections"] });
      }
      qc.invalidateQueries({ queryKey: ["mm-feed", type] });
      navigate({ to: "/se-duyen" });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Không thực hiện được"),
  });

  return (
    <main className="app-feature-page app-feature-bg app-feature-bg--se-duyen flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl px-3 py-6 pb-28 md:py-10">
        <div className="mb-4">
          <Link to="/se-duyen">
            <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
              <ArrowLeft className="h-4 w-4" /> Quay lại
            </Button>
          </Link>
        </div>

        {q.isLoading || !q.data ? (
          q.isError ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Không tìm thấy hồ sơ này.
            </p>
          ) : (
            <Skeleton className="h-96 w-full rounded-2xl" />
          )
        ) : (
          <div className="rounded-2xl border border-gold-soft/40 bg-card/60 p-3 sm:p-5 shadow-mystic w-full max-w-full overflow-hidden min-w-0">
            <FullProfileBody profile={q.data} />

            <div className="mt-6 flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="lg"
                disabled={swipe.isPending}
                onClick={() => swipe.mutate("pass")}
                className="h-14 w-14 rounded-full border-border/60 p-0 shadow-sm transition hover:scale-110 hover:border-rose-500/60 hover:text-rose-400 hover:bg-rose-500/5 active:scale-95"
              >
                <X className="h-6 w-6" />
              </Button>
              <Button
                size="lg"
                disabled={swipe.isPending}
                onClick={() => swipe.mutate("like")}
                className="h-16 w-16 rounded-full bg-gold-gradient p-0 text-primary-foreground shadow-gold transition hover:scale-110 hover:brightness-110 active:scale-95"
              >
                {swipe.isPending ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <Heart className="h-7 w-7" />
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
